import { hasConsent, ConsentCategories } from '../consent';
import { getStoredAttribution, clearAttribution, subscribeToAttribution } from './attribution';
import {
  ATTRIBUTION_TOUCH_PATH,
  ATTRIBUTION_COOKIE_DOMAIN_SUFFIX,
  BeaconOutcome,
} from './constants';
import type { BeaconOutcomeValue } from './constants';
import type { AttributionUnsubscribe, StoredAttribution } from './types';

/**
 * The handoff — the browser giving its staged attribution to the server, which
 * then owns it.
 *
 * This is the layer that makes the ownership model in
 * docs/analytics/attribution-foundation.md real rather than aspirational. Until
 * it runs, the campaign that brought a visitor exists only in a page-writable
 * cookie that Safari's ITP will delete in about a week. After it runs, the same
 * campaign lives in a server-set `HttpOnly` record that survives, and the
 * browser copy is deleted because a second, forgeable copy of the same truth is
 * worse than none.
 *
 * FOUR RULES, and every one of them is a data-loss bug if it is broken:
 *
 *   1. SEND ONLY WITH CONSENT. Attribution is advertising data on the MARKETING
 *      category. The server checks this again on its own; this check is not
 *      redundant, it is the difference between 'the server declined' and 'the
 *      request was never made'.
 *   2. SEND ONCE PER STAGED RECORD. One attempt per page load, and a successful
 *      handoff erases the record — so an ordinary return visit sends nothing at
 *      all. Traffic is one request per campaign touch, not per page view.
 *   3. CLEAR ONLY ON 204. Any other answer — offline, 500, 404, 400 — keeps the
 *      record and retries on the next page load. Never clear data the server may
 *      not have.
 *   4. NEVER SEND WHERE COOKIES CANNOT RIDE. See `isSameSiteEndpoint`. This one
 *      is the difference between 'attribution is not switched on yet' and
 *      'attribution is being destroyed on every visit'.
 *
 * It performs NO analytics. It emits no event, enriches nothing, and reads no
 * dataLayer. Its entire job is to move one record across one boundary.
 *
 * TypeScript rendition of landing-page/src/lib/attribution/beacon.js.
 */

/** Options accepted by the beacon. */
export interface BeaconOptions {
  /** API base URL. Absent → capture-only, no requests. */
  apiUrl?: string;
  /** Injectable transport (tests). */
  fetchImpl?: typeof fetch;
}

/**
 * Whether an attempt has already been made this page load.
 *
 * Module-scoped, like the capture guard in `attribution.ts`, so it survives
 * component mount/unmount. Set only once a request is actually about to leave —
 * the early returns above it (no consent yet, nothing staged) must NOT spend the
 * attempt, because consent can be granted seconds later and that flush is
 * exactly the moment the record first becomes sendable.
 */
let attempted = false;

/**
 * Unsubscribe handle for the attribution subscription, or `null` when the beacon
 * has not been initialized. Doubles as the 'already initialized' flag.
 */
let unsubscribeFromAttribution: AttributionUnsubscribe | null = null;

/** One warning per page load per cause. A loop of identical warnings is noise. */
let warnedNotConfigured = false;
let warnedCrossSite = false;

/**
 * Whether a host sits inside the shared registrable domain.
 *
 * A strict suffix test — `agapae.gr` itself or something under it. Deliberately
 * stricter than the `hostname.includes(...)` the cookie writer carries over from
 * the consent foundation, which would also accept `agapae.gr.example.com`. This
 * predicate decides whether to send personal data, so it fails closed.
 */
function isSharedDomainHost(host: string): boolean {
  return (
    host === ATTRIBUTION_COOKIE_DOMAIN_SUFFIX ||
    host.endsWith(`.${ATTRIBUTION_COOKIE_DOMAIN_SUFFIX}`)
  );
}

/**
 * Whether the API is same-SITE with the page, and therefore whether this handoff
 * can work at all.
 *
 * THIS IS A GUARD AGAINST SILENT DATA DESTRUCTION, not a nicety. Both attribution
 * cookies are `SameSite=Lax`. On a cross-site request the browser sends neither
 * the consent cookie nor the staging cookie and rejects any `Set-Cookie` coming
 * back. The server would therefore see a visitor with no consent, refuse to
 * store, and answer 204 — the same 204 it sends on success, because the endpoint
 * deliberately reveals nothing about a cookie the page may not read. The beacon
 * would then delete a staging record that was never handed to anyone.
 *
 * So the precondition is checked HERE, before sending, rather than inferred from
 * a response that cannot express it. The server's own `warnIfUnscopableHost`
 * makes the same fact visible from the other side.
 *
 * Same-site, not same-origin: ports and schemes do not matter to `SameSite`, so
 * `localhost:5173` → `localhost:4000` is fine in development, and
 * `app.agapae.gr` → `api.agapae.gr` is fine in production. An API on a bare
 * `*.run.app` host is not.
 */
export function isSameSiteEndpoint(
  apiUrl: string | undefined | null,
  hostname: string | undefined | null,
): boolean {
  if (typeof apiUrl !== 'string' || !apiUrl) return false;
  // A root-relative base is same-origin by construction, and same-origin is
  // always same-site.
  if (apiUrl.startsWith('/')) return true;

  let endpointHost: string;
  try {
    endpointHost = new URL(apiUrl).hostname.toLowerCase();
  } catch {
    return false;
  }

  const pageHost = typeof hostname === 'string' ? hostname.toLowerCase() : '';
  if (!endpointHost || !pageHost) return false;
  if (endpointHost === pageHost) return true;
  return isSharedDomainHost(endpointHost) && isSharedDomainHost(pageHost);
}

/** Join the configured base URL to the endpoint path, tolerating a trailing slash. */
function endpointUrl(apiUrl: string): string {
  return apiUrl.replace(/\/+$/, '') + ATTRIBUTION_TOUCH_PATH;
}

/**
 * The request body, built from the stored record and nothing else.
 *
 * Deliberately a straight projection: the beacon does not reshape, enrich, or
 * re-time anything it sends. Whatever the server decides about this payload, it
 * decides about what the browser actually captured.
 *
 * `lastTouch` is omitted rather than sent as `null` when the record has none —
 * a single-touch visitor, or one whose second touch the size guard shed. The
 * endpoint's schema treats it as optional and infers it from `firstTouch`.
 */
function buildPayload(stored: StoredAttribution): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    firstTouch: stored.firstTouch,
    touchCount: stored.touchCount,
  };
  if (stored.lastTouch) payload.lastTouch = stored.lastTouch;
  return payload;
}

/**
 * Hand the staged record to the server, once.
 *
 * Every refusal returns before `attempted` is set, so nothing but a real request
 * spends the one attempt this page load gets.
 */
export async function sendPendingAttribution(
  options: BeaconOptions = {},
): Promise<BeaconOutcomeValue> {
  if (attempted) return BeaconOutcome.ALREADY_ATTEMPTED;

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return BeaconOutcome.UNAVAILABLE;
  }
  const send =
    options.fetchImpl ||
    (typeof fetch === 'function' ? (...args: Parameters<typeof fetch>) => fetch(...args) : null);
  if (!send) return BeaconOutcome.UNAVAILABLE;

  const apiUrl = options.apiUrl;
  if (!apiUrl) {
    if (!warnedNotConfigured) {
      warnedNotConfigured = true;
      console.warn(
        '[attribution][beacon] no API base URL supplied — attribution is captured ' +
          'but never handed to the server. See docs/analytics/attribution-foundation.md.',
      );
    }
    return BeaconOutcome.NOT_CONFIGURED;
  }

  // Rule 1. The server checks consent too, and the two checks answer different
  // questions: this one decides whether a request is made at all.
  if (!hasConsent(ConsentCategories.MARKETING)) return BeaconOutcome.CONSENT_DENIED;

  // The ONLY read of the record, through the module's own semantic API — the
  // cookie format is `cookie.ts`'s business and nothing here knows it exists.
  const stored = getStoredAttribution();
  if (!stored || !stored.firstTouch) return BeaconOutcome.NOTHING_STAGED;

  // Rule 4, and the reason it comes last: it is worth warning about only when
  // there is genuinely something we would otherwise have sent.
  if (!isSameSiteEndpoint(apiUrl, window.location.hostname)) {
    if (!warnedCrossSite) {
      warnedCrossSite = true;
      console.warn(
        `[attribution][beacon] API "${apiUrl}" is not same-site with ` +
          `"${window.location.hostname}". Cookies cannot ride a cross-site request, so the ` +
          `handoff is skipped and the staged record kept. Serve the API from ` +
          `api.${ATTRIBUTION_COOKIE_DOMAIN_SUFFIX}. See docs/analytics/attribution-foundation.md.`,
      );
    }
    return BeaconOutcome.CROSS_SITE;
  }

  // Set synchronously, before the first await, so two callers in the same tick
  // (an init and the consent flush it triggers) cannot both send.
  attempted = true;

  let response: Response;
  try {
    response = await send(endpointUrl(apiUrl), {
      method: 'POST',
      // Without this the cookies do not ride the request: no consent record, so
      // the server refuses to store, and no existing record, so its first-touch
      // freeze has nothing to protect. One word, and attribution silently
      // measures nothing — which is why the parity script asserts it.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload(stored)),
      // Survives an immediate navigation. A campaign click is very often
      // followed within a second by the visitor clicking through.
      keepalive: true,
    });
  } catch (err) {
    // Rule 3. Offline, DNS, CORS, a cancelled request: keep the record.
    console.warn('[attribution][beacon-failed]', (err as Error)?.message);
    return BeaconOutcome.FAILED;
  }

  if (!response || response.status !== 204) {
    // Includes 400 (a payload this server will never accept) and 404 (a path
    // rename). Both keep the record: retrying costs one request per page load,
    // and clearing would destroy a campaign we cannot recover.
    console.warn(`[attribution][beacon-rejected] HTTP ${response ? response.status : 'none'}`);
    return BeaconOutcome.FAILED;
  }

  // Rule 3, the other half. 204 means the server has it — see `isSameSiteEndpoint`
  // for why that inference is sound. From here the browser is not a source of
  // truth for this record, so it must not keep one.
  clearAttribution();
  return BeaconOutcome.SENT;
}

/**
 * Wire the handoff up: attempt now, and again if consent arrives later.
 *
 * The two triggers cover the two ways a record comes to exist. A returning
 * visitor who already consented has one the moment the page loads; a visitor
 * who clicks an ad and then answers the banner has one only after the flush,
 * which `subscribeToAttribution` reports. This is the consumer that subscription
 * was built for.
 *
 * Idempotent, like `initAttribution` and `initConsentMode`: a second call
 * returns the existing subscription rather than adding a listener.
 */
export function initAttributionBeacon(options: BeaconOptions = {}): AttributionUnsubscribe {
  if (unsubscribeFromAttribution) return unsubscribeFromAttribution;

  // Subscribe before the first attempt: a successful attempt clears the record,
  // which notifies listeners, and the re-entrant call must find a live guard
  // rather than a half-built one.
  const unsubscribe = subscribeToAttribution(() => {
    void sendPendingAttribution(options);
  });
  unsubscribeFromAttribution = () => {
    unsubscribe();
    unsubscribeFromAttribution = null;
  };

  void sendPendingAttribution(options);

  return unsubscribeFromAttribution;
}

/**
 * Return the beacon to its pre-`initAttributionBeacon()` state: drop the
 * subscription, the once-per-load attempt guard, and the warning latches.
 *
 * Exists for tests, which need each case to start from a clean slate — all four
 * are module-scoped and would otherwise leak between them. Exported from the
 * module but NOT from the public barrel, so application code cannot reach it by
 * accident.
 */
export function resetBeaconForTests(): void {
  if (unsubscribeFromAttribution) unsubscribeFromAttribution();
  unsubscribeFromAttribution = null;
  attempted = false;
  warnedNotConfigured = false;
  warnedCrossSite = false;
}
